/* Pocket Factory · P2-FLOW-1, the one design record and the page's two stages, Customize and Review
   (docs/p2/DESIGN-RECORD.md is the contract). window.PFDesign:
   - get(): the card being made, as one plain object, read from the page's own sources every time: the order form's
     fields (what the cart line posts), PocketDesign.page (assets/pocket-design.js), the card renderer
     (PocketPersonalization.cards), the lookup's brand and PocketPurpose. It keeps no state of its own, so it can never
     disagree with the card drawn or the order sent.
   - set(patch): each key the way its control would (PocketDesign.page.apply, the Edge color radios, the back upload's
     Remove, cards.setCardAccent). The cart line, the 3D viewer, the Buy gate and the print follow.
   - subscribe(fn): fn(record, changedKeys, previous) after each change; also the document event pf:design.
   - steps(), stage(), go(stage, step): the one ordered list of steps (the editor's [data-pfe-section]s inside
     [data-pf-stage="customize"], then review) and moving between the two stages; also the document event pf:stage. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (!root.PFDesign) { root.PFDesign = api; api.start(root); }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';
  const VERSION = 1;
  const STAGES = ['customize', 'review'];
  /** The record's keys, in the contract's order. */
  const KEYS = ['business', 'website', 'logo', 'front', 'size', 'base', 'color', 'gradient', 'accent', 'orientation', 'art', 'back', 'backArt', 'purpose', 'link', 'finish', 'edge', 'pack', 'ready', 'hold', 'rev', 'stage'];
  /** Written through PocketDesign.page.apply, as the editor's chips, a chat or a call write them. */
  const APPLIED = ['pack', 'finish', 'orientation', 'front', 'base', 'color', 'size', 'purpose', 'link', 'website'];
  /** Keys set() cannot write, and where the shopper changes them instead. */
  const READ_ONLY = {
    business: 'The business comes from the lookup in the Front step.',
    logo: 'The logo comes from the Front step: the logos found, or an upload.',
    gradient: 'The gradient follows the colour style (Gradient, not Flat), on every card: Brand color, White or Black (COLOR-1).',
    art: 'Full-front artwork is made or uploaded in the Front step.',
    backArt: 'An own back is uploaded in the Back step.',
    ready: 'Ready is the Buy gate’s answer.',
    hold: 'Hold is what Buy waits for.',
    rev: 'rev counts edits.',
  };
  const hex = (v) => (/^#[\da-f]{6}$/i.test(v || '') ? String(v).toLowerCase() : null);

  /**
   * The record from the page's sources (pure, so it can be tested without a page).
   * @param {object} src - page: PocketDesign.page.get(); config: cards.getConfig(); identity: cards.getIdentity();
   *   brand; backArt; backgroundArt; style: cards.getCardStyle(); colorStyle: cards.getColorStyle(); edge; stage
   */
  function compose(src = {}) {
    const page = src.page || {}, config = src.config || {}, brand = src.brand || null;
    const mark = (src.identity?.layers || []).find((l) => l && l.kind === 'image' && l.url && (l.role === 'mark' || l.role === 'combined'));
    const art = src.backgroundArt && src.backgroundArt.url ? { url: src.backgroundArt.url, fit: src.backgroundArt.fit === 'fit' ? 'fit' : 'fill', own: !!page.art?.own } : null;
    const back = src.backArt && src.backArt.url ? { url: src.backArt.url, fit: src.backArt.fit === 'fit' ? 'fit' : 'fill' } : null;
    const pack = Number(page.pack) || null;
    return {
      business: String(page.businessName || brand?.businessName || ''),
      website: String(page.website || brand?.websiteUrl || ''),
      logo: mark ? String(mark.url) : null,
      front: page.front || config.logoMode || 'Logo Mark',
      size: Number(page.size || config.logoSize) || 100,
      base: page.base || config.baseColor || 'Light',
      color: hex(page.color),
      // P1-GRAD-2 and COLOR-1: the colour style is every card's (Brand color, White or Black: theirs a subtle tone of their
      // own colour). The front is a gradient in the Gradient style, solid in Flat, and neither under full-front art. Without
      // the card's own colour style (an older card renderer), the brand style says it, as before.
      gradient: !art && (src.colorStyle ? src.colorStyle === 'gradient' : !!src.style && src.style.colorStyle !== 'flat'),
      accent: hex(src.style?.accent),
      orientation: page.orientation || config.orientation || 'Horizontal',
      art,
      back: back ? 'own' : 'standard',
      backArt: back,
      purpose: page.purpose || 'Reviews',
      // Only a link a tap can open: with none yet the order's Destination is the review platform's name ("Google").
      link: /^https?:\/\//i.test(String(page.link || '')) ? String(page.link) : '',
      finish: page.finish || config.finish || 'Matte',
      edge: String(src.edge || ''),
      pack,
      ready: !!page.ready,
      hold: String(page.hold || ''),
      rev: Number(page.rev) || 0,
      stage: STAGES.includes(src.stage) ? src.stage : 'customize',
    };
  }
  /** The keys whose values differ between two records (all of them when there was none before). */
  function changed(before, after) {
    if (!before) return KEYS.slice();
    return KEYS.filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]));
  }

  // ---------- In the browser ----------
  let win = null, doc = null, current = 'customize', last = null, queued = false;
  const listeners = new Set();
  const form = () => doc?.querySelector('[data-pocket-product] form.product-form') || doc?.querySelector('form.product-form') || null;
  const cards = () => win?.PocketPersonalization?.cards || null;
  const designPage = () => win?.PocketDesign?.page || null;
  const safe = (fn, fallback = null) => { try { return fn(); } catch (_) { return fallback; } };

  function get() {
    if (!doc) return compose({});
    const c = cards();
    return compose({
      page: safe(() => designPage()?.get?.()),
      config: safe(() => c?.getConfig?.()),
      identity: safe(() => c?.getIdentity?.()),
      brand: safe(() => win.PocketPersonalization?.store?.getState?.().brand),
      backArt: safe(() => c?.getBackArt?.()),
      backgroundArt: safe(() => c?.getBackgroundArt?.()),
      style: safe(() => c?.getCardStyle?.()),
      colorStyle: safe(() => c?.getColorStyle?.()),
      edge: form()?.querySelector('[data-pf-cc-value]:checked')?.value || '',
      stage: current,
    });
  }

  function press(input, source) {
    if (!input || input.disabled) return false;
    if (!input.checked) {
      input.checked = true;
      const event = new win.Event('change', { bubbles: true });
      event.pfChosen = source;
      input.dispatchEvent(event);
    }
    return true;
  }
  const writers = {
    edge(value, source) {
      const radios = [...(form()?.querySelectorAll('[data-pf-cc-value]') || [])];
      if (!radios.length) return 'This card has no edge choice.';
      return press(radios.find((r) => r.value.toLowerCase() === String(value).toLowerCase()), source) ? '' : 'Edges are ' + radios.map((r) => r.value).join(' or ') + '.';
    },
    accent(value) {
      const c = cards();
      if (!c?.setCardAccent || !c.getCardStyle?.()) return 'The accent is only for a brand-styled card with the standard back.';
      const want = value === null ? null : hex(value);
      if (value !== null && !want) return 'Use a colour like #1f2a44.';
      c.setCardAccent(want);
      return want && hex(c.getCardStyle()?.accent) !== want ? 'That accent isn’t one of this card’s choices.' : '';
    },
    back(value) {
      const own = !!cards()?.getBackArt?.();
      if (value === 'standard') {
        if (own) doc.querySelector('[data-pfbu-remove]')?.click();
        return cards()?.getBackArt?.() ? 'The back couldn’t be changed. Try Remove in the Back step.' : '';
      }
      if (value === 'own') return own ? '' : 'Upload your own back in the Back step.';
      return 'The back is standard or own.';
    },
    stage(value) { return go(value) ? '' : 'There is no ' + value + ' stage on this page.'; },
  };
  /** Applies the keys in the order given; resolves to {ok, record, errors}. Never throws. */
  async function set(patch = {}, { source = 'chip' } = {}) {
    const errors = {};
    for (const [key, value] of Object.entries(patch || {})) {
      let error = '';
      try {
        if (APPLIED.includes(key)) {
          const page = designPage();
          if (!page?.apply) error = 'This page has no design to change.';
          else { const answer = await page.apply(key, value, { source }); if (!answer?.ok) error = answer?.error || 'That couldn’t be changed.'; }
        } else if (writers[key]) error = writers[key](value, source);
        else error = READ_ONLY[key] || 'There is no ' + key + ' on the card.';
      } catch (_) { error = 'That couldn’t be changed. Try again.'; }
      if (error) errors[key] = error;
    }
    notify();
    return { ok: !Object.keys(errors).length, record: get(), errors };
  }

  /** Calls fn(record, changedKeys, previous) after each change; returns the unsubscribe. */
  function subscribe(fn) {
    if (typeof fn !== 'function') return () => {};
    listeners.add(fn);
    return () => listeners.delete(fn);
  }
  /** Compares the record with the last one heard, once a frame at most, and tells every listener what changed. */
  function notify() {
    if (queued || !win) return;
    queued = true;
    const run = () => {
      queued = false;
      const now = get(), keys = changed(last, now);
      if (last && !keys.length) return;
      const before = last;
      last = now;
      for (const fn of listeners) { try { fn(now, keys, before); } catch (_) {} }
      doc.dispatchEvent(new win.CustomEvent('pf:design', { detail: { record: now, changed: keys } }));
    };
    if (win.requestAnimationFrame) win.requestAnimationFrame(run); else win.setTimeout(run, 16);
  }

  /** The one ordered list of steps: the editor's steps inside Customize, in the page's order, then Review. */
  function steps() {
    if (!doc) return [];
    const customize = doc.querySelector('[data-pf-stage="customize"]');
    const list = [...(customize?.querySelectorAll('[data-pfe-section]') || [])].map((section) => ({
      stage: 'customize', step: section.dataset.pfeSection, label: (section.querySelector('.pfe-name')?.textContent || section.dataset.pfeSection).trim(),
    }));
    const review = doc.querySelector('[data-pf-stage="review"]');
    if (review) list.push({ stage: 'review', step: 'review', label: (review.querySelector('[data-pf-stage-title]')?.textContent || 'Review').trim() });
    return list;
  }
  const stage = () => current;
  function become(next) {
    if (!STAGES.includes(next) || next === current) return;
    const from = current;
    current = next;
    doc.documentElement.dataset.pfStageCurrent = next;
    doc.dispatchEvent(new win.CustomEvent('pf:stage', { detail: { stage: next, from } }));
    notify();
  }
  /** Brings a stage (and one of its steps) into view and gives it focus; false when the page has no such stage. */
  function go(name, step) {
    if (!doc || !STAGES.includes(name)) return false;
    const node = doc.querySelector(`[data-pf-stage="${name}"]`);
    if (!node || node.hidden) return false;
    const reduced = !!win.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    locked = Date.now() + (reduced ? 200 : 1200);
    become(name);
    if (name === 'customize') {
      // No card yet (the editor is off): its steps aren't on the page. Finish & pack goes to the pack list the page shows
      // meanwhile (pocket-cro.js's jump), any other step to the box that makes their card (assets/pf-use-case.js), as
      // the guided Buy does (LONGRUN-1102 round 2: "Change" did nothing on the example card).
      if (!node.querySelector('[data-pfe][data-pfe-on]')) {
        const jump = step === 'finish' ? doc.querySelector('form [data-pfx-jump="pack"]') : null;
        if (jump) { jump.click(); return true; }
        if (win.PocketUseCase?.design?.go) { win.PocketUseCase.design.go(); return true; }
      }
      // The editor opens the step (on a phone the one open section) and focuses its heading, as its own progress row does.
      const key = step || node.querySelector('[data-pfe-section]')?.dataset.pfeSection;
      const button = key && node.querySelector(`[data-pfe-go="${key}"]`);
      if (button && !button.closest('[hidden]')) { button.click(); return true; }
      const heading = (key && node.querySelector(`[data-pfe-section="${key}"] .pfe-title`)) || node.querySelector('.pfe-title');
      heading?.scrollIntoView?.({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
      heading?.focus?.({ preventScroll: true });
      return true;
    }
    const heading = node.querySelector('[data-pf-stage-title]') || node;
    heading.scrollIntoView?.({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
    heading.focus?.({ preventScroll: true });
    return true;
  }

  let started = false;
  function start(w) {
    if (started || !w?.document) return;
    started = true;
    win = w;
    doc = w.document;
    doc.documentElement.dataset.pfStageCurrent = current;
    const go = () => { try { wire(); } catch (_) {} };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', go, { once: true });
    else go();
  }
  function wire() {
    // Every edit the page announces, and the controls themselves.
    for (const type of ['pocket:card-config', 'pocket:design-change', 'pocket:design-hold', 'pocket:design-drawn', 'pocket:design-reset', 'pocket:cart-line', 'pocket:purpose-chosen', 'pocket:preview-hydrated', 'change', 'input']) doc.addEventListener(type, notify);
    // The lookup's brand (the store arrives with its own deferred script).
    let subscribed = false;
    const listen = () => { const store = win.PocketPersonalization?.store; if (subscribed || !store?.subscribe) return; subscribed = true; store.subscribe(notify); };
    listen();
    win.addEventListener('load', () => { listen(); notify(); }, { once: true });
    if (typeof win.MutationObserver === 'function') {
      const watch = new win.MutationObserver(notify);
      for (const node of doc.querySelectorAll('[data-pfbu], [data-buy-bar]')) watch.observe(node, { attributes: true, attributeFilter: ['data-pfbu-on', 'data-pfbu-state', 'data-state', 'data-hold', 'data-gate'] });
    }
    // Buttons that move between stages, wherever they are.
    doc.addEventListener('click', (event) => {
      const button = event.target?.closest?.('[data-pf-go-stage],[data-pf-go-step]');
      if (!button) return;
      const moved = go(button.dataset.pfGoStage || 'customize', button.dataset.pfGoStep);
      if (moved) event.preventDefault();
      if (moved && button.dataset.pfGoStage === 'review') remember();
    });
    // LONGRUN-1102 round 3 (a developer: Back from Review showed the box before Preview, the designer gone): "Review
    // your cards" is a step in the browser's history too, over the saved card's own entry (pocket-card-profile.js, whose
    // popstate keeps the designer for it), so Back goes to Finish & pack and Forward to Review again. Only Back from that
    // entry goes to Finish & pack: an entry another part of the page adds and takes back (the review sheet's own, closed
    // with Esc) lands where it started, and a Review reached by scrolling stays put.
    let onReview = false;
    try { onReview = win.history.state?.pfStage === 'review'; } catch (_) {}
    function remember() {
      try {
        const now = win.history.state;
        if (now?.pf === 'card' && now.pfStage !== 'review') win.history.pushState({ ...now, pfStage: 'review' }, '', win.location.href);
        onReview = win.history.state?.pfStage === 'review';
      } catch (_) {}
    }
    win.addEventListener('popstate', (event) => {
      const state = event.state, left = onReview;
      onReview = state?.pfStage === 'review';
      if (state?.pf !== 'card') return;
      if (onReview) { if (current !== 'review') go('review'); }
      else if (left && current === 'review') go('customize', 'finish');
    });
    stages();
    notify();
  }

  /* ---------- The old second configurator's carriers ---------- */
  // Its fields stay in the order form ([data-pf-carrier]: steps 01 and 02, the design fields), because the editor writes
  // the cart line through them. With the editor on the page pocket-cro.css takes them out of sight, never the hidden
  // attribute: pocket.js reads an input under a hidden ancestor as not given (its link would not reach the line). Whatever
  // would open one goes to the Customize step that owns that choice instead, so there is one editing flow and Review
  // opens nothing in place: a contact card's details too, which the Link step asks (CONSOL-1b).
  const STEP_OF = (target) => {
    if (target.closest('[data-pfbu]')) return 'back';
    if (target.closest('[data-pf-cc]') || target.closest('[data-card-field="finish"]')) return 'finish';
    if (target.closest('[data-pfx-step="destination"]')) return 'link';
    return 'front';
  };
  function carriers(customize) {
    // A control revealed in a carrier (pocket.js, pf-buy-guide.js, pf-use-case.js: pocket:reveal-control), before the
    // order form's own handler would open the hidden panel and scroll to nothing.
    doc.addEventListener('pocket:reveal-control', (event) => {
      const target = event.detail?.target, carrier = target?.closest?.('[data-pf-carrier]');
      if (!carrier) return;
      event.stopImmediatePropagation();
      const step = STEP_OF(target);
      go('customize', step);
      // A link asked for (the Buy gate's link step, the order box's link): the Link step's own field takes it, so they can
      // paste at once (their Google place search first on a review card); else the review line's Change, which opens that
      // search; else its choices. A contact card's detail asked for (the Buy gate's, "Add contact details"): the Link
      // step's own field for it (CONSOL-1b; data-pfq-contact-field, the part of its name after "Contact "), with More
      // options opened when it is there, else the Contact answer. Their logo asked for (step 01's upload, the Buy gate's
      // "Add your logo"): the Front step's own upload, the logo strip's tile or "Upload your logo".
      const detail = /^properties\[Contact (.+)\]$/.exec(target.getAttribute?.('name') || '')?.[1];
      const own = detail && customize.querySelector(`[data-pfq-contact-field="${detail}"]`);
      // A contact card whose Link step still shows another answer (its type set by a script): the Contact answer first.
      if (own && own.closest('[hidden]') && win.PocketPurpose?.get?.() === 'Contact') win.PocketCardProfile?.page?.answer?.('contact');
      if (own) own.closest('[data-pfq-more]')?.querySelector('[data-pfq-mo][aria-expanded="false"]')?.click();
      const wanted = own ? [`[data-pfq-contact-field="${detail}"]`, '[data-pfq-contact-field]', '[data-pfq-choice][value="contact"]']
        : step === 'link' ? ['#PfqPlace', '#PfqLink', '[data-pfq-place-change]', '[data-pfq-choice]:checked', '[data-pfq-choice]']
        : target.closest('[data-pfu="step"]') ? ['[data-pfq-strip-upload]', '[data-pfq-upload]'] : null;
      if (wanted) {
        const shown = (node) => !!node && !node.disabled && !node.closest('[hidden]') && node.getClientRects().length > 0;
        const reach = () => {
          const field = wanted.map((s) => customize.querySelector(s)).find(shown);
          if (!field) return;
          // At once, as the order form's own reveal does (a field far up the page, its focus already there).
          field.focus({ preventScroll: true });
          field.scrollIntoView?.({ block: 'center', behavior: 'instant' });
        };
        reach();
        // The step's own script may redraw it just after it opens (question 2 swaps its place line for the link field
        // when the link is their Google listing): if what had focus went with it, focus goes to what shows now.
        win.setTimeout(() => {
          const at = doc.activeElement;
          if (!at || at === doc.body || !customize.contains(at) || !shown(at)) reach();
        }, 300);
      }
    }, true);
    // The hero's "Change design" (the logo fit note, assets/pocket-card.js) opened "Personalize design" 3,000px down: now
    // the Front step's own Color choice, right there (COLOR-1: its one Color container, no chip to open), in view with
    // the colour on the card focused.
    doc.addEventListener('click', (event) => {
      const link = event.target?.closest?.('[data-logo-fit-note] [data-fit-action="picked"]');
      if (!link || !customize.contains(link)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      go('customize', 'front');
      const color = customize.querySelector('[data-pfcol]');
      if (!color) return;
      // On a phone, its title just under the pinned card (its scroll-margin, assets/pf-exp-editor.css; LONGRUN-1102 round
      // 3: centred, the title went under the card and the tiles were cut in half).
      const pinned = !!win.matchMedia?.('(max-width: 1023.98px)').matches;
      color.scrollIntoView?.({ block: pinned ? 'start' : 'center', behavior: win.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      (color.querySelector('[data-pfcol-base] input:checked') || color.querySelector('[data-pfcol-base] input'))?.focus({ preventScroll: true });
    }, true);
    // A Buy with no business on a page without the guided Buy shows step 01's field (pocket.js askBusiness): the hero's
    // own field is where a business is added now.
    const ask = doc.querySelector('[data-pfx-business-ask]');
    if (ask && typeof win.MutationObserver === 'function') {
      new win.MutationObserver(() => {
        if (ask.hidden || !ask.closest('[data-pf-carrier]')) return;
        const field = doc.getElementById('ReviewsWebsite');
        if (field && field.getClientRects().length) {
          become('customize');
          field.scrollIntoView?.({ block: 'center', behavior: win.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          field.focus({ preventScroll: true });
        } else go('customize', 'front');
      }).observe(ask, { attributes: true, attributeFilter: ['hidden'] });
    }
  }

  /* ---------- The two stages on the page ---------- */
  // Both are there when the hero's editor (Customize) and the order form's Review are: then Review shows its heading, the
  // recap of the steps and the way back, and the last step offers the way on. Without the editor the order form stays
  // as it was (data-pf-stages="off").
  let locked = 0;
  function stages() {
    const customize = doc.querySelector('[data-pf-stage="customize"]'), editor = customize?.querySelector('[data-pfe]');
    const review = doc.querySelector('[data-pf-stage="review"]');
    const on = !!(editor && review);
    doc.documentElement.dataset.pfStages = on ? 'on' : 'off';
    // No editor: the order form's own steps and its design fields, in place, are the page's one configurator (the CSS
    // that hides the carriers asks for the editor too).
    if (!on) return;
    carriers(customize);
    for (const node of review.querySelectorAll('[data-pf-stage-chrome]')) node.hidden = false;
    const next = customize.querySelector('[data-pf-stage-next]');
    // The recap's words are the editor's own one-line summaries (assets/pf-exp-editor.js writes them).
    const recap = () => {
      for (const row of review.querySelectorAll('[data-pf-recap-step]')) {
        const text = (editor.querySelector(`[data-pfe-section="${row.dataset.pfRecapStep}"] [data-pfe-summary]`)?.textContent || '').trim();
        const out = row.querySelector('[data-pf-recap-value]');
        if (out && out.textContent !== text) out.textContent = text;
        row.hidden = !editor.querySelector(`[data-pfe-section="${row.dataset.pfRecapStep}"]`);
      }
      // The way on shows with the steps (the editor is on once there is a card).
      if (next) next.hidden = !editor.hasAttribute('data-pfe-on');
    };
    if (typeof win.MutationObserver === 'function') {
      const watch = new win.MutationObserver(recap);
      watch.observe(editor, { attributes: true, attributeFilter: ['data-pfe-on', 'hidden'] });
      for (const summary of editor.querySelectorAll('[data-pfe-summary]')) watch.observe(summary, { childList: true, characterData: true, subtree: true });
    }
    recap();
    // Where the shopper is: the stage most in view, except just after go() (its own scroll is still under way).
    if (typeof win.IntersectionObserver === 'function') {
      const seen = new Map();
      const io = new win.IntersectionObserver((entries) => {
        for (const entry of entries) seen.set(entry.target, entry.isIntersecting ? entry.intersectionRect.height : 0);
        if (Date.now() < locked) return;
        const r = seen.get(review) || 0, c = seen.get(customize) || 0;
        if (r || c) become(r > c ? 'review' : 'customize');
      }, { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });
      io.observe(customize);
      io.observe(review);
    }
  }

  return { version: VERSION, STAGES, KEYS, APPLIED, compose, changed, start, get, set, subscribe, notify, steps, stage, go };
});
